import {Box, Flex, Text, Divider, Image} from "@chakra-ui/react";
import {useMemo} from "react";

const OrderSummary = ({tickets, data, t}) => {
    const isFree = !!data?.isFree;
    const quantities = data?.cart?.quantities || {};

    const grossPrice = (ticket) =>
        ticket.price * (1 + ticket.vatPercentage / 100);

    const summary = useMemo(() => {
        if (isFree) {
            const qty = Math.max(0, Number(data?.cart?.quantity ?? 0));
            const name =
                tickets?.[0]?.name || t?.("ticket.standard") || "Bilet standardowy";
            return qty > 0 ? [{ name, quantity: qty, isFree: true, price: 0 }] : [];
        }
        return (tickets || [])
            .filter((tt) => (quantities[tt.id] || 0) > 0)
            .map((tt) => ({
                name: tt.name,
                quantity: quantities[tt.id],
                price: grossPrice(tt),
                isFree: false,
            }));
    }, [isFree, data?.cart?.quantity, tickets, quantities, t]);

    const totalPrice = !isFree
        ? summary?.reduce((sum, item) => sum + item.quantity * item.price, 0)
        : 0;

    return (
        <Flex direction="column" w="100%" maxW={{base: "100%", md: "380px"}} gap="4">
            <Flex direction="column" bg="#F8F5F4" borderRadius="8px" p="16px" gap="32px">
                <Text fontFamily="body" fontWeight="600" fontSize="22px" mb="16px">
                    {t?.(isFree ? "your_reservation" : "your_order") ||
                        (isFree ? "Twoja rezerwacja" : "Twoje zamówienie")}
                </Text>

                <Flex direction="column" gap="8px">
                    {summary?.map((item, idx) => (
                        <Flex key={idx} justify="space-between">
                            <Flex direction={"row"} gap="8px">
                                <Text fontFamily="body" fontWeight="700" fontSize="18px">
                                    {item.quantity}x
                                </Text>
                                <Text>
                                    {item.name}
                                </Text>
                            </Flex>
                            <Text fontFamily="body" fontWeight="500" fontSize="18px" style={{'text-wrap': 'nowrap'}}>
                                {item.isFree
                                ? t?.("free") || "Bezpłatny"
                                : `${item.price.toFixed(2)} zł`}
                            </Text>
                        </Flex>
                    ))}
                </Flex>
                {!data?.isFree &&
                    (<>
                        <Divider my="12px" borderColor="#DED3D4"/>

                        <Flex justify="space-between" fontWeight="bold">
                            <Text>{t('order.form.summary')}:</Text>
                            <Text style={{'text-wrap': 'nowrap'}}>{totalPrice?.toFixed(2)} zł ({t('order.form.gross')})</Text>
                        </Flex>
                    </>
                )}
            </Flex>

            {!data?.isFree && (
                <Flex
                    flexDirection={"column"}
                    alignItems={"center"}
                    bg="#F8F5F4"
                    borderRadius="8px"
                    px="16px"
                    py="32px"
                >
                    <Text mb="8px">
                        {t('order.form.payment.title')}
                    </Text>
                    <svg width="180" height="48" viewBox="0 0 180 48" fill="none" xmlns="http://www.w3.org/2000/svg" style={{"justify-self": "center"}}>
                        <path
                            d="M21.5752 0.00292969H7.59084C6.63371 0.00292969 5.81996 0.699639 5.67063 1.64639L0.0146851 37.5725C-0.0978072 38.2812 0.450323 38.9205 1.16794 38.9205H7.84431C8.80127 38.9205 9.61503 38.2242 9.76435 37.2755L11.2898 27.5858C11.4371 26.637 12.2528 25.9405 13.2078 25.9405H17.6348C26.8467 25.9405 32.1632 21.4745 33.5519 12.6246C34.1774 8.75266 33.5782 5.7106 31.7685 3.58006C29.781 1.24034 26.2557 0.00292969 21.5752 0.00292969ZM23.1886 13.1245C22.4238 18.1518 18.5898 18.1518 14.8825 18.1518H12.7723L14.2527 8.76306C14.3407 8.19553 14.8314 7.77763 15.404 7.77763H16.3711C18.8964 7.77763 21.2788 7.77763 22.5096 9.2197C23.2438 10.0802 23.4688 11.3585 23.1886 13.1245Z"
                            fill="#283B82"/>
                        <path
                            d="M63.3768 12.9626H56.6806C56.11 12.9626 55.6172 13.3805 55.529 13.948L55.2329 15.8245L54.7645 15.1443C53.3148 13.0362 50.0818 12.3317 46.8551 12.3317C39.4548 12.3317 33.1343 17.9468 31.9034 25.8237C31.2635 29.7529 32.1734 33.51 34.3982 36.13C36.4387 38.539 39.3587 39.543 42.8332 39.543C48.7958 39.543 52.102 35.702 52.102 35.702L51.8036 37.5663C51.6908 38.2793 52.2391 38.9183 52.9528 38.9183H58.9846C59.9439 38.9183 60.7537 38.2217 60.9051 37.2733L64.524 14.3105C64.6388 13.6037 64.0928 12.9626 63.3768 12.9626ZM54.0425 26.0204C53.3967 29.8532 50.3599 32.4265 46.4871 32.4265C44.5427 32.4265 42.9883 31.8015 41.9904 30.6174C41.0009 29.4415 40.6246 27.7678 40.9394 25.9035C41.5426 22.1034 44.6306 19.4465 48.4438 19.4465C50.3456 19.4465 51.8914 20.0794 52.9099 21.2736C53.9303 22.4802 54.3349 24.1642 54.0425 26.0204Z"
                            fill="#283B82"/>
                        <path
                            d="M99.0424 12.9626H92.3132C91.6711 12.9626 91.0678 13.2822 90.7038 13.817L81.4224 27.5139L77.4883 14.3516C77.2408 13.528 76.4825 12.9626 75.6234 12.9626H69.0107C68.2068 12.9626 67.6489 13.7492 67.9044 14.5072L75.3167 36.3L68.3482 46.156C67.7999 46.9324 68.3519 48 69.2991 48H76.0204C76.6582 48 77.2554 47.6884 77.6171 47.164L99.9996 14.7962C100.535 14.0217 99.9853 12.9626 99.0424 12.9626Z"
                            fill="#283B82"/>
                        <path
                            d="M121.323 0.00274658H107.337C106.382 0.00274658 105.568 0.699456 105.419 1.64621L99.7627 37.5723C99.6503 38.281 100.198 38.9203 100.912 38.9203H108.089C108.756 38.9203 109.327 38.433 109.43 37.769L111.036 27.5856C111.183 26.6368 111.999 25.9403 112.954 25.9403H117.379C126.593 25.9403 131.907 21.4743 133.298 12.6244C133.925 8.75248 133.322 5.71041 131.513 3.57988C129.527 1.24015 126.004 0.00274658 121.323 0.00274658ZM122.937 13.1243C122.174 18.1516 118.34 18.1516 114.631 18.1516H112.522L114.005 8.76288C114.093 8.19535 114.579 7.77745 115.154 7.77745H116.121C118.645 7.77745 121.029 7.77745 122.26 9.21951C122.994 10.08 123.217 11.3583 122.937 13.1243Z"
                            fill="#469BDB"/>
                        <path
                            d="M163.123 12.9626H156.431C155.856 12.9626 155.367 13.3805 155.281 13.948L154.985 15.8245L154.515 15.1443C153.065 13.0362 149.834 12.3317 146.607 12.3317C139.207 12.3317 132.889 17.9468 131.658 25.8237C131.02 29.7529 131.926 33.51 134.15 36.13C136.195 38.539 139.111 39.543 142.585 39.543C148.548 39.543 151.854 35.702 151.854 35.702L151.556 37.5663C151.443 38.2793 151.991 38.9183 152.709 38.9183H158.739C159.694 38.9183 160.508 38.2217 160.657 37.2733L164.279 14.3105C164.389 13.6037 163.841 12.9626 163.123 12.9626ZM153.789 26.0204C153.147 29.8532 150.106 32.4265 146.233 32.4265C144.293 32.4265 142.735 31.8015 141.736 30.6174C140.747 29.4415 140.375 27.7678 140.686 25.9035C141.293 22.1034 144.376 19.4465 148.19 19.4465C150.092 19.4465 151.637 20.0794 152.656 21.2736C153.68 22.4802 154.085 24.1642 153.789 26.0204Z"
                            fill="#469BDB"/>
                        <path
                            d="M171.018 0.988394L165.278 37.5723C165.166 38.281 165.714 38.9203 166.427 38.9203H172.198C173.157 38.9203 173.971 38.224 174.118 37.2753L179.778 1.35128C179.891 0.642177 179.343 0.0010376 178.629 0.0010376H172.167C171.597 0.00274658 171.106 0.421197 171.018 0.988394Z"
                            fill="#469BDB"/>
                    </svg>
                </Flex>
            )}
        </Flex>
    );
};

export default OrderSummary;
